import { lazy, Suspense } from "react"
import { Navigate, Route, Routes, useLocation } from "react-router-dom"
import { useAuth } from "./auth/AuthContext"
import { RIGA_ROLES, Roles } from "./auth/session"
import Layout from "./components/Layout"
import { LanguageProvider } from "./lib/language"
import LoginScreen from "./screens/LoginScreen"
import GroupsScreen from "./screens/GroupsScreen"
import GroupScreen from "./screens/GroupScreen"
import NewGroupScreen from "./screens/NewGroupScreen"
import Dictionary from "./screens/Dictionary"
import Publish from "./screens/Publish"
import GuidesScreen from "./screens/GuidesScreen"
import type { ReactElement } from "react"

// Шрифты бренд-каталога (PF Din Text Cond Pro + Myriad Pro, ~1,3 МБ .ttf на
// оба) и `@progress/kendo-drawing` грузятся только этим экраном — он нужен
// не на каждом заходе, поэтому в отдельном чанке, остальные пять экранов эту
// массу не тянут вовсе.
const PdfScreen = lazy(() => import("./screens/PdfScreen"))

function RequireAuth({ children }: { children: ReactElement }) {
	const { session, hasRole, logout } = useAuth()
	const location = useLocation()

	if (!session) {
		return <Navigate to="/login" replace state={{ from: location.pathname }} />
	}

	// Разделение здесь только визуальное: API общее, источник истины — бэк.
	// Серверные права — отдельный трек (фаза 5-С).
	if (!hasRole(...RIGA_ROLES)) {
		return (
			<div className="login">
				<div className="login__card">
					<div className="login__brand">Доступ закрыт</div>
					<p className="muted">
						Учётная запись {session.username} не входит в рижские роли.
					</p>
					<button className="btn" onClick={logout}>
						Выйти
					</button>
				</div>
			</div>
		)
	}

	return children
}

/**
 * Экран «Каталоги и сайты» (`/publish`) — только администратору (решение
 * заказчика).
 *
 * Проверка визуальная, как и остальные: источник истины — бэк. Смысл её в
 * другом — здесь назначают товары в каталоги и заводят новые сайты, кладя в
 * ERP боевые реквизиты ФТП и БД сайта; промах стоит дорого. Загрузка одной
 * группы из карточки этим не закрыта — она живёт во вкладке «Сайт», открытой
 * всем, кто может публиковать.
 */
function RequireAdmin({ children }: { children: ReactElement }) {
	const { hasRole } = useAuth()
	return hasRole(Roles.Admin) ? children : <Navigate to="/groups" replace />
}

export default function App() {
	return (
		<Routes>
			<Route path="/login" element={<LoginScreen />} />
			<Route
				element={
					<RequireAuth>
						<LanguageProvider>
							<Layout />
						</LanguageProvider>
					</RequireAuth>
				}
			>
				<Route path="/groups" element={<GroupsScreen />} />
				<Route path="/groups/new" element={<NewGroupScreen />} />
				<Route path="/groups/:id" element={<GroupScreen />} />
				<Route
					path="/publish"
					element={
						<RequireAdmin>
							<Publish />
						</RequireAdmin>
					}
				/>
				{/* «Переводы» в старом фронте — страница с кнопками, ведущими
				    в справочник и форму переводчика. Форма влилась в справочник
				    (символы, ссылки и выгрузка — переключателями), так что обоих
				    старых адресов больше нет: ведём в справочник. */}
				<Route
					path="/translate"
					element={<Navigate to="/translate/dictionary" replace />}
				/>
				<Route
					path="/translate/form"
					element={<Navigate to="/translate/dictionary" replace />}
				/>
				<Route path="/translate/dictionary" element={<Dictionary />} />
				{/* Видео-гайды открыты всем рижским ролям — это справка, не действие */}
				<Route path="/guides" element={<GuidesScreen />} />
				<Route path="/guides/:id" element={<GuidesScreen />} />
				<Route
					path="/pdf"
					element={
						<Suspense
							fallback={
								<div className="state">
									<span className="spinner" />
								</div>
							}
						>
							<PdfScreen />
						</Suspense>
					}
				/>
			</Route>
			<Route path="*" element={<Navigate to="/groups" replace />} />
		</Routes>
	)
}
